import { z } from 'zod';

export const INPUT_TYPES = ['pass_fail', 'temperature'] as const;
export type InputType = (typeof INPUT_TYPES)[number];

export const templateItemSchema = z.object({
  key: z.string().min(1),
  text: z.string().min(1),
  points: z.number().min(0).default(1),
  input_type: z.enum(INPUT_TYPES).default('pass_fail'),
  // Temperature items: a reading inside [temp_min, temp_max] passes, outside fails.
  temp_min: z.number().nullable().default(null),
  temp_max: z.number().nullable().default(null),
  temp_unit: z.enum(['F', 'C']).default('F'),
  // Checked at publish time (validateTemplateContent) so half-typed links can autosave.
  sop_url: z.string().max(2000).nullable().default(null),
  allow_na: z.boolean().default(true),
  allow_comment: z.boolean().default(true),
  allow_photo: z.boolean().default(true),
  allow_video: z.boolean().default(false),
  is_critical: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
});
export type TemplateItem = z.infer<typeof templateItemSchema>;

export const templateSectionSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  items: z.array(templateItemSchema),
});
export type TemplateSection = z.infer<typeof templateSectionSchema>;

export const scoringRulesSchema = z.object({
  passing_threshold: z.number().min(0).max(100).default(85),
  warning_threshold: z.number().min(0).max(100).default(70),
  // Weighted: each item counts by its points. Off: every item counts equally.
  use_weighted_scoring: z.boolean().default(false),
});
export type ScoringRules = z.infer<typeof scoringRulesSchema>;

export const laborConfigSchema = z.object({
  enabled: z.boolean().default(true),
  allow_comment: z.boolean().default(true),
  allow_photo: z.boolean().default(true),
  allow_video: z.boolean().default(true),
});
export type LaborConfig = z.infer<typeof laborConfigSchema>;

// The versioned body of a template: what gets frozen on publish.
export const templateContentSchema = z.object({
  sections: z.array(templateSectionSchema),
  scoring_rules: scoringRulesSchema,
  labor_config: laborConfigSchema,
});
export type TemplateContent = z.infer<typeof templateContentSchema>;

export function allItems(content: Pick<TemplateContent, 'sections'>): (TemplateItem & { section_key: string })[] {
  return content.sections.flatMap((s) => s.items.map((i) => ({ ...i, section_key: s.key })));
}

// Checks a template is publishable: unique keys, sane thresholds, temperature ranges set.
export function validateTemplateContent(content: TemplateContent): string[] {
  const errors: string[] = [];
  if (content.sections.length === 0) errors.push('Add at least one section.');
  const sectionKeys = new Set<string>();
  const itemKeys = new Set<string>();
  for (const s of content.sections) {
    if (sectionKeys.has(s.key)) errors.push(`Duplicate section key "${s.key}".`);
    sectionKeys.add(s.key);
    if (s.items.length === 0) errors.push(`Section "${s.label}" has no items.`);
    for (const i of s.items) {
      if (itemKeys.has(i.key)) errors.push(`Duplicate item key "${i.key}".`);
      itemKeys.add(i.key);
      if (i.input_type === 'temperature' && i.temp_min == null && i.temp_max == null) {
        errors.push(`Temperature item "${i.text}" needs a minimum or maximum.`);
      }
      if (i.sop_url && !/^https?:\/\/\S+\.\S+/.test(i.sop_url)) {
        errors.push(`SOP link for "${i.text}" must start with http:// or https://.`);
      }
      if (i.temp_min != null && i.temp_max != null && i.temp_min > i.temp_max) {
        errors.push(`Temperature item "${i.text}" has minimum above maximum.`);
      }
    }
  }
  const r = content.scoring_rules;
  if (r.warning_threshold > r.passing_threshold) errors.push('Warning threshold must be at or below the passing threshold.');
  return errors;
}

export function newKey(prefix: 'sec' | 'item'): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}
